// Print pipeline for <Letterhead>-based documents.
//
// Strategy (verified working across Chrome/Firefox/Safari via window.print()):
//   1. Wrap the document in an outer <table class="doc-page">.
//   2. Put the ENTIRE letterhead (brand strip + title bar + party block —
//      i.e. .lh-header, which already contains those via the pageHead prop)
//      inside <thead>. Browsers natively repeat table-header-group content
//      at the top of every printed page.
//   3. Put an invisible placeholder inside <tfoot> that matches the visible
//      footer's height. table-footer-group reserves that space at the bottom
//      of every page so body content never spills into it.
//   4. Put .lh-body (items + totals + notes + T&C) in <tbody>. Rows inside
//      the items table split naturally at page boundaries.
//   5. Paint the actual visible green footer as a `position: fixed` overlay
//      pinned to `bottom: 0` — this stays anchored to the paper bottom on
//      every page, INCLUDING the last (short) page where the tfoot would
//      otherwise float mid-page.
//   6. Orange rail + centred watermark are also `position: fixed` so they
//      draw on every printed page.
//
// The @page margin is zero — all visual breathing room is applied inside the
// letterhead's own padding + the fixed footer's height.

// -----------------------------------------------------------------------------
// printDom.js — Reliable multi-page DOM print engine.
// -----------------------------------------------------------------------------

import { toast } from "sonner";

/**
 * CSS layout styles injected directly into the printing iframe context.
 */
const A4_STYLES = `
  body, body * { visibility: visible !important; }

  @page { 
    size: A4 portrait; 
    margin: 0; /* Keeps baseline bounds clean so fixed background designs match pixels */
  }
  
  html, body {
    margin: 0; 
    padding: 0; 
    background: #ffffff;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* --- 1. THE CANVAS PAGINATION MATRIX --- */
  table.doc-page {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    page-break-inside: auto !important;
  }
  
  table.doc-page > thead { 
    display: table-header-group !important; 
  }
  
  table.doc-page > tfoot { 
    display: table-footer-group !important; 
  }
  
  table.doc-page > tbody,
  table.doc-page > tbody > tr,
  table.doc-page > tbody > tr > td {
    page-break-inside: auto !important;
    vertical-align: top;
    padding: 0;
    border: 0;
  }

  /* --- 2. HEADER CONTAINER & EXTRACTIONS --- */
  .lh-header-full-group {
    display: block !important;
    width: 100% !important;
    background: #ffffff !important;
    box-sizing: border-box !important;
    page-break-inside: avoid !important;
  }

  /* --- 3. DYNAMIC TARGET SELECTORS --- */
  /* Ensure original metadata wrappers inside running canvas hide when printing */
  .lh-body-content-print > .lh-body-metadata-source {
    display: none !important;
  }

  /* Dynamic conditional margin spacer triggered exclusively on Page 2 onwards */
  .page-two-top-spacer {
    display: none;
  }

  @media print {
    table.doc-page > thead > tr.spacer-row {
      display: none !important;
    }
    table.doc-page > thead ~ thead > tr.spacer-row {
      display: table-row !important;
    }
    .page-two-top-spacer {
      display: block !important;
      height: 20mm !important;
    }
  }

  /* --- 4. PRINT FLOW AND PADDING WRAPPERS --- */
  .lh-header-full-group {
    padding: 10mm 10mm 2mm 10mm !important;
  }

  .lh-body-content-print {
    padding: 5mm 10mm 5mm 10mm !important;
    position: relative !important;
    z-index: 2 !important;
    background: transparent !important;
    page-break-inside: auto !important;
  }

  /* --- 5. RE-ANCHORED TEMPLATE WIREFRAME LAYER --- */
  .lh-footer-fixed {
    position: fixed !important;
    bottom: 0; 
    left: 0mm; 
    right: 0;
    height: 25mm;
    padding: 4mm 15mm 4mm 21mm !important;
    box-sizing: border-box !important;
    background: #2b5c34 !important; 
    color: #ffffff !important;
    z-index: 100 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .lh-footer-fixed, .lh-footer-fixed * { 
    color: #ffffff !important; 
  }

  .print-rail {
    position: fixed !important;
    top: 0; 
    bottom: 0; 
    left: 0mm;
    width: 3px;
    background: #f97316 !important;
    z-index: 101 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .print-borders-overlay {
    position: fixed !important;
    top: 0;
    bottom: 0;
    left: 6px; 
    right: 0;
    pointer-events: none !important;
    z-index: 99 !important; 
  }

  .lh-watermark {
    position: fixed !important;
    top: 0; left: 0; right: 0; bottom: 0;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 1 !important;
    pointer-events: none;
    overflow: hidden;
  }
  
  .lh-watermark img {
    opacity: 0.09 !important;
    width: 420px !important;
    height: 420px !important;
    max-width: 55% !important;
  }

  .lh-body-content-print table { 
    page-break-inside: auto !important; 
    width: 100%; 
  }
  
  .lh-body-content-print tr { 
    page-break-inside: avoid !important; 
    page-break-after: auto !important; 
  }
  
  .lh-body-content-print td, .lh-body-content-print th {
    page-break-inside: avoid !important;
  }

  .print\\:hidden { 
    display: none !important; 
  }
`;

export function printDom(selector = '[data-testid="letterhead"]', title = "Print") {
  let node = document.querySelector(selector);
  
  if (!node) {
    node = document.querySelector('[data-testid="doc-view"]') || document.querySelector('.letterhead-a4');
  }

  if (!node) {
    toast.error("Nothing to print — please open the document view first.");
    return false;
  }

  const headHtml = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((n) => n.outerHTML)
    .join("\n");

  // Clone target element cleanly
  const cloned = node.cloneNode(true);
  cloned.removeAttribute("style");

  const printableHtml = rewritePaginationMatrix(cloned);
  const baseHref = window.location.origin + "/";

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<base href="${baseHref}">
<title>${escapeHtml(title)}</title>
${headHtml}
<style>${A4_STYLES}</style>
</head>
<body>
<div class="print-root">${printableHtml}</div>
<script>
  window.addEventListener('load', function(){
    setTimeout(function(){
      try { 
        window.focus(); 
        window.print(); 
      } catch(e) { 
        console.error('Print trigger failed:', e); 
      }
    }, 300);
  });
</script>
</body>
</html>`;

  const iframe = document.createElement("iframe");
  iframe.setAttribute("data-testid", "print-iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";

  document.body.appendChild(iframe);
  iframe.srcdoc = html;

  setTimeout(() => {
    try {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    } catch (_) { }
  }, 60000);

  return true;
}

/**
 * Modern CSS-driven declarative layout matrix constructor.
 */
function rewritePaginationMatrix(cloned) {
  const targetRoot = cloned.classList.contains("letterhead-a4") 
    ? cloned 
    : (cloned.querySelector(".letterhead-a4") || cloned);

  const header = targetRoot.querySelector(".lh-header");
  const body = targetRoot.querySelector(".lh-body");
  const footer = targetRoot.querySelector(".lh-footer");
  const watermark = targetRoot.querySelector(".lh-watermark");

  if (!header || !body || !footer) {
    return cloned.innerHTML;
  }

  // 1. ISOLATE METADATA BLOCK SAFELY VIA ELEMENT DOM STRUCTURING
  const mainTable = body.querySelector("table");
  let metadataHtml = "";
  
  if (mainTable) {
    const originalBodyNodes = Array.from(body.childNodes);
    const tableIndex = originalBodyNodes.indexOf(mainTable);
    const metadataNodes = originalBodyNodes.slice(0, tableIndex);
    
    const metadataWrapper = document.createElement("div");
    metadataWrapper.className = "lh-body-metadata-source";
    
    metadataNodes.forEach(n => {
      const copy = n.cloneNode(true);
      metadataWrapper.appendChild(copy);
    });
    
    metadataHtml = metadataWrapper.innerHTML;
    
    // Inject custom class target rule to hide the raw source metadata from the main table's body stream
    metadataNodes.forEach(n => {
      if (n.nodeType === Node.ELEMENT_NODE) {
        n.classList.add("lh-body-metadata-source");
      } else if (n.nodeType === Node.TEXT_NODE && n.textContent.trim()) {
        const span = document.createElement("span");
        span.className = "lh-body-metadata-source";
        n.parentNode.insertBefore(span, n);
        span.appendChild(n);
      }
    });
  }

  // 2. CONSTRUCT REPEATING CONTENT AND ASSETS
  const repeatedHeaderGroupHtml = `
    <div class="lh-header-full-group">
      ${header.innerHTML}
      ${metadataHtml ? `<div style="margin-top: 4mm;">${metadataHtml}</div>` : ""}
    </div>
  `;

  const wmHtml = watermark ? watermark.outerHTML : "";
  const footerInner = footer.innerHTML;
  const fixedFooterHtml = `<div class="lh-footer-fixed">${footerInner}</div>`;

  // 3. BUILD AND RETURN THE NATIVE SYSTEM DOCUMENT TABLE LAYOUT
  return `
    <div class="print-rail"></div>
    <div class="print-borders-overlay"></div>
    ${wmHtml}
    ${fixedFooterHtml}
    <table class="doc-page">
      <thead>
        <tr class="spacer-row" style="display: none;">
          <td style="padding: 0; border: 0;">
            <div class="page-two-top-spacer" style="height: 20mm;"></div>
          </td>
        </tr>
        <tr>
          <td style="padding: 0; border: 0;">
            ${repeatedHeaderGroupHtml}
          </td>
        </tr>
      </thead>
      <tfoot>
        <tr>
          <td style="padding: 0; border: 0;">
            <div style="height: 26mm; visibility: hidden;"></div>
          </td>
        </tr>
      </footer>
      <tbody>
        <tr>
          <td style="padding: 0; border: 0;">
            <div class="lh-body-content-print">
              ${body.innerHTML}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  `;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}